import React, { useState } from 'react';
import { Plus } from 'lucide-react';

interface AddCategoryFormProps {
  onAddCategory: (name: string, color: string, targetPercentage?: number, limitAmount?: number) => void;
  totalBudget?: number;
  totalAllocatedPercentage?: number;
  currentCategoryCount: number;
  isPremium: boolean;
}

const colorOptions = [
  { name: 'Indigo', color: '#6366F1' },
  { name: 'Red', color: '#EF4444' },
  { name: 'Green', color: '#10B981' },
  { name: 'Blue', color: '#3B82F6' },
  { name: 'Yellow', color: '#F59E0B' },
  { name: 'Purple', color: '#8B5CF6' },
  { name: 'Pink', color: '#EC4899' },
  { name: 'Teal', color: '#14B8A6' },
  { name: 'Orange', color: '#F97316' },
  { name: 'Gray', color: '#6B7280' },
];

export function AddCategoryForm({ 
  onAddCategory, 
  totalBudget, 
  totalAllocatedPercentage = 0,
  currentCategoryCount,
  isPremium
}: AddCategoryFormProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [name, setName] = useState('');
  const [targetPercentage, setTargetPercentage] = useState<string>('');
  const [limitAmount, setLimitAmount] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState('#6366F1');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const MAX_FREE_CATEGORIES = 4;
  const isLimitReached = !isPremium && currentCategoryCount >= MAX_FREE_CATEGORIES;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const calculatedAmount = targetPercentage && totalBudget 
    ? (parseFloat(targetPercentage) / 100) * totalBudget 
    : null;

  const remainingPercentage = 100 - totalAllocatedPercentage;
  const newRemainingPercentage = targetPercentage 
    ? remainingPercentage - parseFloat(targetPercentage) 
    : remainingPercentage;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Category name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Pass target percentage and limit amount if provided
      const percentage = targetPercentage ? parseFloat(targetPercentage) : undefined;
      const limit = limitAmount ? parseFloat(limitAmount) : undefined;
      
      onAddCategory(name.trim(), selectedColor, percentage, limit);
      
      setName('');
      setTargetPercentage('');
      setLimitAmount('');
      setSelectedColor('#6366F1');
      setIsExpanded(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add category');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setName('');
    setTargetPercentage('');
    setLimitAmount('');
    setSelectedColor('#6366F1');
    setError(null);
    setIsExpanded(false);
  };

  if (!isExpanded) {
    if (isLimitReached) {
      return (
        <div className="w-full flex flex-col items-center justify-center px-4 py-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800/50">
          <p className="text-gray-600 dark:text-gray-400 font-medium mb-2">
            Free plan limit reached ({currentCategoryCount}/{MAX_FREE_CATEGORIES})
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-3">
            Upgrade to Premium to add unlimited categories.
          </p>
          <a 
            href="/pricing" 
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            Upgrade Now
          </a>
        </div>
      );
    }

    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-blue-500 dark:hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
      >
        <Plus className="h-5 w-5 mr-2" />
        Add New Category
      </button>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Add New Category</h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}
        
        {/* Category name */}
        <div>
          <label htmlFor="categoryName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Category Name
          </label>
          <input
            id="categoryName"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter category name..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        </div>

        {/* Target Percentage */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="targetPercentage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Target % of Income
            </label>
            <div className="relative">
              <input
                id="targetPercentage"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={targetPercentage}
                onChange={(e) => setTargetPercentage(e.target.value)}
                placeholder="e.g. 20"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span className="text-gray-500 dark:text-gray-400 sm:text-sm">%</span>
              </div>
            </div>
            {calculatedAmount !== null && (
              <p className="mt-1 text-xs text-blue-600 dark:text-blue-400 font-medium">
                Equals: {formatCurrency(calculatedAmount)}
              </p>
            )}
            {totalBudget && (
              <p className={`mt-0.5 text-xs ${newRemainingPercentage < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                Remaining: {newRemainingPercentage.toFixed(1)}%
              </p>
            )}
          </div>

          <div>
            <label htmlFor="limitAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Limit Amount ($)
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 dark:text-gray-400 sm:text-sm">$</span>
              </div>
              <input
                id="limitAmount"
                type="number"
                min="0"
                step="0.01"
                value={limitAmount}
                onChange={(e) => setLimitAmount(e.target.value)}
                placeholder="e.g. 500"
                className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Set a target percentage OR a fixed limit amount. Limit amount takes precedence.
        </p>

        {/* Color selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Choose Color
          </label>
          <div className="grid grid-cols-5 gap-3">
            {colorOptions.map((option) => (
              <button
                key={option.color}
                type="button"
                onClick={() => setSelectedColor(option.color)}
                className={`w-10 h-10 rounded-full border-2 hover:scale-110 transition-transform ${
                  selectedColor === option.color
                    ? 'border-gray-900 dark:border-white ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-gray-800'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                style={{ backgroundColor: option.color }}
                title={option.name}
              />
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 pt-4 border-t dark:border-gray-700">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !name.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Adding...' : 'Add Category'}
          </button>
        </div>
      </form>
    </div>
  );
}